import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AdminLayout from "./AdminLayout";
import { Users, Eye, CheckCircle, XCircle, Search, Filter, Bell, RefreshCw, Trash2 } from "lucide-react";
import "../styles/Admin.css";
import { getAllUsers, verifyuser, getPendingPasswordResets, adminResetPassword, deleteUser } from "../ServiceAPI/AdminUser/api";

const UsersPage = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [verificationFilter, setVerificationFilter] = useState("all");
  const [pendingResets, setPendingResets] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [resettingUserId, setResettingUserId] = useState(null);

  // Fetch users from backend
  useEffect(() => {
    async function fetchUsers() {
      try {
        const data = await getAllUsers();
        console.log("getAllUsers response:", data);
        if (Array.isArray(data)) {
          setUsers(data);
        } else if (Array.isArray(data.users)) {
          setUsers(data.users);
        } else if (Array.isArray(data.data)) {
          setUsers(data.data);
        } else {
          setUsers([]);
        }
      } catch (err) {
        setUsers([]);
      } finally {
        setLoading(false);
      }
    }
    fetchUsers();
  }, []);

  // Fetch pending password resets
  useEffect(() => {
    async function fetchPendingResets() {
      try {
        const data = await getPendingPasswordResets();
        console.log("getPendingPasswordResets response:", data);
        if (Array.isArray(data)) {
          setPendingResets(data);
        } else if (Array.isArray(data.users)) {
          setPendingResets(data.users);
        } else {
          setPendingResets([]);
        }
      } catch (err) {
        console.error("Error fetching pending resets:", err);
        setPendingResets([]);
      }
    }
    fetchPendingResets();

    // Refresh every 30 seconds
    const interval = setInterval(fetchPendingResets, 30000);
    return () => clearInterval(interval);
  }, []);

  // Check authentication
  useEffect(() => {
    const isAdminAuthenticated = localStorage.getItem("adminAuth") === "true";
    if (!isAdminAuthenticated) {
      navigate("/admin/login");
    }
  }, [navigate]);

  // Toggle verification status
  const toggleVerification = async (userId) => {
    try {
      const user = users.find(u => u.userId === userId);
      if (!user) return;

      const newStatus = {
        isEmailVerified: parseInt(user.isEmailVerified) === 1 ? 0 : 1,
        isPhoneVerified: parseInt(user.isPhoneVerified) === 1 ? 0 : 1
      };

      await verifyuser(userId, newStatus);

      const data = await getAllUsers();
      if (Array.isArray(data)) {
        setUsers(data);
      } else if (Array.isArray(data.users)) {
        setUsers(data.users);
      } else if (Array.isArray(data.data)) {
        setUsers(data.data);
      } else {
        setUsers([]);
      }
    } catch (error) {
      console.error("Error toggling verification status:", error);
    }
  };

  const handleViewProfile = (userId) => {
    localStorage.setItem("viewProfileUserId", userId);
    navigate(`/admin/user/${userId}`);
  };

  // Handle user deletion
  const handleDeleteUser = async (userId) => {
    const user = users.find(u => u.userId === userId);
    if (!user) return;

    if (!window.confirm(`Are you sure you want to delete ${user.Username}? This action cannot be undone.`)) {
      return;
    }

    try {
      await deleteUser(userId);
      alert("User deleted successfully!");

      // Refresh users list
      const data = await getAllUsers();
      if (Array.isArray(data)) {
        setUsers(data);
      } else if (Array.isArray(data.users)) {
        setUsers(data.users);
      } else if (Array.isArray(data.data)) {
        setUsers(data.data);
      } else {
        setUsers([]);
      }
    } catch (error) {
      console.error("Error deleting user:", error);
      alert(error.response?.data?.error || "Failed to delete user. Please try again.");
    }
  };

  // Handle password reset
  const handleResetPassword = async (userId) => {
    if (!window.confirm("Are you sure you want to reset this user's password to 111111? An email will be sent to the user.")) {
      return;
    }

    setResettingUserId(userId);
    try {
      const result = await adminResetPassword(userId);
      alert(result.message || "Password reset successfully and email sent to user!");

      // Refresh pending resets list
      const data = await getPendingPasswordResets();
      if (Array.isArray(data)) {
        setPendingResets(data);
      } else if (Array.isArray(data.users)) {
        setPendingResets(data.users);
      } else {
        setPendingResets([]);
      }

      // Close notifications if no more pending resets
      if (pendingResets.length <= 1) {
        setShowNotifications(false);
      }
    } catch (error) {
      console.error("Error resetting password:", error);
      alert(error.response?.data?.error || "Failed to reset password. Please try again.");
    } finally {
      setResettingUserId(null);
    }
  };

  // Filter and search users
  const filteredUsers = users.filter(user => {
    // Search filter
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch =
      !searchTerm ||
      (user.Username && user.Username.toLowerCase().includes(searchLower)) ||
      (user.Email && user.Email.toLowerCase().includes(searchLower)) ||
      (user.Phone && user.Phone.toLowerCase().includes(searchLower)) ||
      (user.StateName && user.StateName.toLowerCase().includes(searchLower));

    // Verification filter
    const isVerified = parseInt(user.isEmailVerified) === 1 && parseInt(user.isPhoneVerified) === 1;
    const matchesVerification =
      verificationFilter === 'all' ||
      (verificationFilter === 'verified' && isVerified) ||
      (verificationFilter === 'unverified' && !isVerified);

    return matchesSearch && matchesVerification;
  });

  if (loading) {
    return (
      <AdminLayout>
        <div className="loading-container">
          <div className="spinner-large"></div>
          <p>Loading users...</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="admin-page-header">
        <div>
          <h1 className="admin-page-title">Users</h1>
          <p className="admin-page-subtitle">Manage registered users</p>
        </div>

        {/* Notification Icon */}
        <div className="notification-container" style={{ position: 'relative' }}>
          <button
            className="notification-bell-btn"
            onClick={() => setShowNotifications(!showNotifications)}
            style={{
              position: 'relative',
              background: '#2563eb',
              color: 'white',
              border: 'none',
              borderRadius: '50%',
              width: '50px',
              height: '50px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              boxShadow: '0 2px 8px rgba(37, 99, 235, 0.3)',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'scale(1.1)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(37, 99, 235, 0.5)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'scale(1)';
              e.currentTarget.style.boxShadow = '0 2px 8px rgba(37, 99, 235, 0.3)';
            }}
          >
            <Bell size={24} />
            {pendingResets.length > 0 && (
              <span style={{
                position: 'absolute',
                top: '-5px',
                right: '-5px',
                background: '#dc2626',
                color: 'white',
                borderRadius: '50%',
                width: '24px',
                height: '24px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '12px',
                fontWeight: 'bold',
                border: '2px solid white'
              }}>
                {pendingResets.length}
              </span>
            )}
          </button>

          {/* Notification Panel */}
          {showNotifications && (
            <div style={{
              position: 'absolute',
              top: '60px',
              right: '0',
              width: '420px',
              maxHeight: '600px',
              overflowY: 'auto',
              background: 'var(--bg-color, #ffffff)',
              borderRadius: '16px',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15), 0 8px 20px rgba(0, 0, 0, 0.08)',
              zIndex: 1000,
              border: '1px solid var(--border-color, #e2e8f0)',
              backdropFilter: 'blur(10px)'
            }}>
              {/* Header */}
              <div style={{
                padding: '20px 24px 16px',
                borderBottom: '1px solid var(--border-light-color, #f1f5f9)',
                background: 'var(--header-gradient, linear-gradient(135deg, #667eea 0%, #764ba2 100%))',
                borderRadius: '16px 16px 0 0',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <div>
                  <h3 style={{
                    margin: '0 0 4px 0',
                    fontSize: '18px',
                    fontWeight: '700',
                    color: 'white',
                    letterSpacing: '-0.025em'
                  }}>
                    Password Reset Requests
                  </h3>
                  <p style={{
                    margin: 0,
                    fontSize: '13px',
                    color: 'rgba(255, 255, 255, 0.8)',
                    fontWeight: '400'
                  }}>
                    Users awaiting password reset approval
                  </p>
                </div>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px'
                }}>
                  <span style={{
                    background: 'rgba(255, 255, 255, 0.2)',
                    backdropFilter: 'blur(10px)',
                    color: 'white',
                    padding: '6px 12px',
                    borderRadius: '20px',
                    fontSize: '12px',
                    fontWeight: '600',
                    border: '1px solid rgba(255, 255, 255, 0.3)'
                  }}>
                    {pendingResets.length}
                  </span>
                  <button
                    onClick={() => setShowNotifications(false)}
                    style={{
                      background: 'rgba(255, 255, 255, 0.1)',
                      border: 'none',
                      borderRadius: '8px',
                      color: 'white',
                      padding: '8px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)'}
                  >
                    ×
                  </button>
                </div>
              </div>

              {/* Content */}
              <div style={{
                maxHeight: '480px',
                overflowY: 'auto',
                padding: pendingResets.length === 0 ? '0' : '8px 0'
              }}>
                {pendingResets.length === 0 ? (
                  <div style={{
                    padding: '60px 32px',
                    textAlign: 'center',
                    color: 'var(--text-muted-color, #64748b)'
                  }}>
                    <div style={{
                      background: 'var(--bg-secondary, #f8fafc)',
                      borderRadius: '50%',
                      width: '80px',
                      height: '80px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto 20px'
                    }}>
                      <Bell size={32} style={{ opacity: 0.4, color: 'var(--text-muted-color, #94a3b8)' }} />
                    </div>
                    <h4 style={{
                      margin: '0 0 8px 0',
                      fontSize: '16px',
                      fontWeight: '600',
                      color: 'var(--text-color, #334155)'
                    }}>
                      All caught up!
                    </h4>
                    <p style={{
                      margin: 0,
                      fontSize: '14px',
                      color: 'var(--text-muted-color, #64748b)',
                      lineHeight: '1.5'
                    }}>
                      No pending password reset requests at the moment
                    </p>
                  </div>
                ) : (
                  pendingResets.map((user, index) => (
                    <div
                      key={user.userId}
                      style={{
                        margin: '8px 16px',
                        padding: '20px',
                        borderRadius: '12px',
                        background: 'var(--card-bg-color, #ffffff)',
                        border: '1px solid var(--border-light-color, #f1f5f9)',
                        transition: 'all 0.2s ease',
                        position: 'relative',
                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.04)'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 8px 25px rgba(0, 0, 0, 0.12)';
                        e.currentTarget.style.borderColor = 'var(--primary-color, #3b82f6)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.04)';
                        e.currentTarget.style.borderColor = 'var(--border-light-color, #f1f5f9)';
                      }}
                    >
                      {/* User Info */}
                      <div style={{ marginBottom: '16px' }}>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px',
                          marginBottom: '12px'
                        }}>
                          <div style={{
                            width: '40px',
                            height: '40px',
                            borderRadius: '50%',
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            fontWeight: '600',
                            fontSize: '16px'
                          }}>
                            {user.Username?.charAt(0).toUpperCase() || 'U'}
                          </div>
                          <div style={{ flex: 1 }}>
                            <div style={{
                              fontWeight: '600',
                              fontSize: '15px',
                              marginBottom: '2px',
                              color: 'var(--text-color, #1e293b)',
                              lineHeight: '1.4'
                            }}>
                              {user.Username}
                            </div>
                            <div style={{
                              fontSize: '12px',
                              color: 'var(--text-muted-color, #64748b)',
                              background: 'var(--bg-secondary, #f8fafc)',
                              padding: '2px 8px',
                              borderRadius: '10px',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px'
                            }}>
                              <span style={{
                                width: '6px',
                                height: '6px',
                                borderRadius: '50%',
                                background: '#f59e0b',
                                display: 'inline-block'
                              }}></span>
                              Pending Reset
                            </div>
                          </div>
                        </div>

                        <div style={{
                          paddingLeft: '52px',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '6px'
                        }}>
                          <div style={{
                            fontSize: '13px',
                            color: 'var(--text-muted-color, #64748b)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px'
                          }}>
                            <span style={{ fontWeight: '500', minWidth: '45px' }}>Email:</span>
                            <span style={{
                              fontFamily: 'monospace',
                              background: 'var(--bg-secondary, #f8fafc)',
                              padding: '2px 6px',
                              borderRadius: '4px',
                              fontSize: '12px'
                            }}>
                              {user.Email}
                            </span>
                          </div>
                          <div style={{
                            fontSize: '13px',
                            color: 'var(--text-muted-color, #64748b)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px'
                          }}>
                            <span style={{ fontWeight: '500', minWidth: '45px' }}>Phone:</span>
                            <span>
                              {user.Phone || 'Not provided'}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Action Button */}
                      <button
                        onClick={() => handleResetPassword(user.userId)}
                        disabled={resettingUserId === user.userId}
                        style={{
                          width: '100%',
                          padding: '12px 16px',
                          background: resettingUserId === user.userId
                            ? 'var(--bg-secondary, #f1f5f9)'
                            : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          color: resettingUserId === user.userId
                            ? 'var(--text-muted-color, #64748b)'
                            : 'white',
                          border: 'none',
                          borderRadius: '10px',
                          cursor: resettingUserId === user.userId ? 'not-allowed' : 'pointer',
                          fontWeight: '600',
                          fontSize: '14px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '8px',
                          transition: 'all 0.2s ease',
                          boxShadow: resettingUserId === user.userId
                            ? 'none'
                            : '0 4px 12px rgba(102, 126, 234, 0.4)',
                          letterSpacing: '0.025em'
                        }}
                        onMouseEnter={(e) => {
                          if (resettingUserId !== user.userId) {
                            e.currentTarget.style.transform = 'translateY(-1px)';
                            e.currentTarget.style.boxShadow = '0 6px 20px rgba(102, 126, 234, 0.5)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (resettingUserId !== user.userId) {
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.4)';
                          }
                        }}
                      >
                        {resettingUserId === user.userId ? (
                          <>
                            <RefreshCw size={16} style={{ animation: 'spin 1s linear infinite' }} />
                            Processing...
                          </>
                        ) : (
                          <>
                            <span>🔄</span>
                            Reset Password & Send Email
                          </>
                        )}
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>

      <div className="admin-content-area">
        <div className="data-table-card">
          <div className="data-table-header">
            <h3>Registered Users ({filteredUsers.length} of {users.length})</h3>
          </div>

          {/* Search and Filter Section */}
          <div className="search-filter-section">
            <div className="search-box">
              <Search size={20} />
              <input
                type="text"
                placeholder="Search by Name, Email, Phone, State..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
            </div>
            <div className="filter-group">
              <Filter size={18} />
              <select
                value={verificationFilter}
                onChange={(e) => setVerificationFilter(e.target.value)}
                className="filter-select"
              >
                <option value="all">All Users</option>
                <option value="verified">Verified</option>
                <option value="unverified">Unverified</option>
              </select>
            </div>
          </div>
          {filteredUsers.length > 0 ? (
            <div className="table-responsive">
              <table className="modern-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Phone</th>
                    <th>Email</th>
                    <th>State</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map(user => (
                    <tr key={user.userId}>
                      <td>{user.Username}</td>
                      <td>{user.Phone}</td>
                      <td>{user.Email}</td>
                      <td>{user.StateName}</td>
                      <td>
                        <span className={`status-badge-modern ${parseInt(user.isEmailVerified) === 1 && parseInt(user.isPhoneVerified) === 1
                            ? 'approved'
                            : 'unverified'
                          }`}>
                          {parseInt(user.isEmailVerified) === 1 && parseInt(user.isPhoneVerified) === 1
                            ? 'Verified'
                            : 'Unverified'}
                        </span>
                      </td>
                      <td>
                        <div className="table-action-group">
                          <button
                            className={`action-btn ${parseInt(user.isEmailVerified) === 1 && parseInt(user.isPhoneVerified) === 1
                                ? 'warning'
                                : 'success'
                              }`}
                            onClick={() => toggleVerification(user.userId)}
                            title={parseInt(user.isEmailVerified) === 1 && parseInt(user.isPhoneVerified) === 1 ? 'Unverify' : 'Verify'}
                          >
                            {parseInt(user.isEmailVerified) === 1 && parseInt(user.isPhoneVerified) === 1
                              ? <XCircle size={16} />
                              : <CheckCircle size={16} />}
                          </button>
                          <button
                            className="action-btn view"
                            onClick={() => handleViewProfile(user.userId)}
                            title="View Profile"
                          >
                            <Eye size={16} />
                          </button>
                          <button
                            className="action-btn delete"
                            onClick={() => handleDeleteUser(user.userId)}
                            title="Delete User"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="empty-state">
              <Users size={48} />
              <p>No users available.</p>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default UsersPage;
